import { recordUndoEvent } from '@/lib/undo-recorder';

/**
 * How an issue — or an undo — reaches a control that is not on screen.
 *
 * `focusIssueField` finds its target with a `data-field-id` query, which works only for a control
 * that is currently MOUNTED. Several of the places an issue points at are pages of a carousel —
 * DataViz shows one series at a time, and the six branching blocks show one branch at a time — so
 * "two series share a label" or "branch 3's answer will not evaluate" names a field that is not in
 * the document while another page is showing. The click did nothing, silently, and the issue row
 * still rendered as a button, which is the exact dead control `IssueRow` documents avoiding.
 *
 * So a carousel registers, for every id on every page including the hidden ones, how to bring that
 * page into view. The reveal runs, React re-renders, and the query is retried on a later frame.
 *
 * Deliberately a registry keyed by field id rather than a `branchIndex` on the issue. The issue
 * already carries the only thing it reliably knows — WHICH control is at fault — and it should not
 * also have to know which surface that control happens to be paginated by. That keeps
 * `BlockIssue.fields` opaque, which is what lets one carousel (branches) and a completely
 * different one (series) use the same mechanism with no shared vocabulary.
 *
 * There is a second registry below, keyed by BLOCK id, for the settings drawer. It cannot be keyed
 * by field id for the reason the drawer exists: its contents are not mounted while it is shut, so
 * nothing inside it is around to register itself. See `registerBlockReveal`.
 */

const revealers = new Map<string, () => void>();

/**
 * Registers how to bring `id`'s page into view. Returns the cleanup, for an effect.
 *
 * Ids are unique across a task (they are option/series/branch ids), so a flat registry is safe
 * without a block-id namespace — the same reasoning `branch-jump.ts` relies on.
 */
export function registerFieldReveal(id: string, reveal: () => void): () => void {
  revealers.set(id, reveal);
  return () => {
    // Guarded: a re-register from a remounting component may already have replaced this entry, and
    // an unguarded delete on the old instance's cleanup would remove the NEW one.
    if (revealers.get(id) === reveal) revealers.delete(id);
  };
}

/** Runs the reveal for `id`, if one is registered. Returns whether anything happened. */
export function revealField(id: string): boolean {
  const reveal = revealers.get(id);
  if (!reveal) return false;
  reveal();
  return true;
}

/**
 * How a block opens and shuts its settings drawer.
 *
 * Registered by `InputBlockFrame`, which owns `settingsOpen`. The drawer's contents are rendered
 * through a portal ONLY while it is open, so a configuration toggle that an undo just took back is
 * not merely off-screen — it is not in the document at all, and no amount of scrolling or paging
 * finds it. That is why this registry is keyed by block rather than by field: there is nothing
 * inside a shut drawer to have registered itself.
 */
export interface BlockRevealer {
  /** Flip to the feedback panel. The frame owns `isEditingFeedback`, so only it can do this. */
  openFeedback: () => void;
  closeFeedback: () => void;
  isFeedbackOpen: () => boolean;
  openSettings: () => void;
  closeSettings: () => void;
  isSettingsOpen: () => boolean;
}

const blockRevealers = new Map<string, BlockRevealer>();

export function registerBlockReveal(blockId: string, r: BlockRevealer): () => void {
  blockRevealers.set(blockId, r);
  return () => {
    if (blockRevealers.get(blockId) === r) blockRevealers.delete(blockId);
  };
}

/**
 * Being in the document is not the same as being reachable.
 *
 * `BlockEditorDrawer` does not unmount a shut drawer — it collapses it with a grid-rows trick, so
 * the controls inside stay mounted at zero height, marked `inert` and `aria-hidden`. A plain
 * `querySelector` finds them, and the ladder used to conclude it had succeeded: the drawer never
 * opened and the pulse landed somewhere nobody could see.
 *
 * `aria-hidden` is the marker rather than `inert`, and the difference matters. The branch carousel
 * makes its panel `inert` while the equation is unresolvable, but leaves it visible at 30% opacity
 * and does NOT mark it hidden — pointing at that branch is still the right answer. "Removed from
 * the accessibility tree" is exactly the claim being tested: not available to the person.
 */
/**
 * Anywhere in the document, reachable or not — but PREFERRING the block the step belongs to.
 *
 * `changed-fields` returns ids unqualified, on a stated assumption that option and series ids are
 * unique across a task. They are not. `primary`, `default-option-1`, `default-option-2` and
 * `default-item-1` are literal constants minted by whatever creates a default block, so any task
 * holding two MCQs — or two Numericals — has blocks sharing field ids. Measured across the dev
 * database, eleven tasks did.
 *
 * `document.querySelector` returns the first match in document order, so an undo in the second
 * block rang a row in the first: a confident, silent lie about what had moved. Qualifying the ids
 * at the source would mean changing what `BlockIssue.fields` means as well, so the disambiguation
 * happens here, where the owner is already known — `pulseFirstField` is given the block id.
 *
 * Falls back to the first match when the owner is unknown or has nothing matching, which keeps
 * every single-block case exactly as it was.
 */
const findAnywhere = (id: string, blockId?: string): HTMLElement | null => {
  const all = document.querySelectorAll<HTMLElement>(`[data-field-id="${CSS.escape(id)}"]`);
  if (all.length < 2 || !blockId) return all[0] ?? null;
  const owned = `[data-reveal-block="${CSS.escape(blockId)}"]`;
  for (const el of all) if (el.closest(owned)) return el;
  return all[0] ?? null;
};

/**
 * Being in the document is not the same as being reachable.
 *
 * `BlockEditorDrawer` does not unmount a shut drawer — it collapses it with a grid-rows trick, so
 * the controls inside stay mounted at zero height, marked `inert` and `aria-hidden`. A plain
 * lookup finds them, and the ladder used to conclude it had succeeded: the drawer never opened and
 * the pulse played on something nobody could see.
 *
 * `aria-hidden` is the marker rather than `inert`, and the difference is load-bearing: the branch
 * carousel makes its panel inert while the equation is unresolvable but leaves it visible at 30%
 * opacity, and pointing at that branch is still right. Removed from the accessibility tree is the
 * claim being tested.
 */
const isReachable = (el: Element): boolean => !el.closest('[aria-hidden="true"]');

/**
 * Already where the teacher is looking?
 *
 * An undo rebuilds the editor panel, and scrolling it as well — to a control that was on screen the
 * whole time — is movement that carries no information. `block: 'nearest'` is not enough on its
 * own: it still scrolls whatever ancestor can scroll when the element is merely near an edge.
 */
export function isInView(el: HTMLElement): boolean {
  const r = el.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return false;
  return r.top >= 0 && r.left >= 0
    && r.bottom <= (window.innerHeight || document.documentElement.clientHeight)
    && r.right <= (window.innerWidth || document.documentElement.clientWidth);
}

/** Bring it into view only if it is not already there. */
export function scrollIntoViewIfNeeded(el: HTMLElement): void {
  if (!isInView(el)) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
}

/** The same ring, on an element that is not a field. A summary feedback block lives in a dialog
 *  and has no `data-field-id` to look up, but a teacher arriving from the problems panel needs
 *  the same "this one" it draws everywhere else. */
export function pulseElement(el: HTMLElement): void { applyRing(el); }

const RING_MS = 700;
/** How long a vanished field might just be remounting. */
const LOST_GRACE_MS = 250;

function applyRing(el: HTMLElement): void {
  // Removed and re-added across a reflow, so pulsing the same field twice actually replays —
  // re-adding a class the element already has animates nothing.
  el.classList.remove('agavi-pulse');
  void el.offsetWidth;
  el.classList.add('agavi-pulse');
  window.setTimeout(() => el.classList.remove('agavi-pulse'), RING_MS);
}

/**
 * Keep the ring on the FIELD, not on the node that happened to be there when it went on.
 *
 * The tree an undo is being explained against is still being rebuilt underneath the ring: the
 * feedback panel mounts what it holds, a collapsed card builds its editor when it expands, and a
 * reveal does both while the highlight is running. So the element the ring went on can be gone a
 * moment later and the highlight vanishes with it. Two reports came from exactly this, back when
 * an undo remounted the whole editor as well — a feedback message showed no ring, because a
 * rebuilt card re-collapses and took the ringed editor with it, and a drawer switch showed none
 * because its checkbox was simply replaced. The remount is gone; the surfaces are not.
 *
 * So the ring is re-asserted for as long as it should be visible, following the field across
 * whatever React does underneath — including re-running the reveal, which is what expands a card
 * that collapsed itself on the way back.
 */
function keepRinged(id: string, ringed: HTMLElement, gen: number, blockId?: string): void {
  const deadline = performance.now() + RING_MS;
  let current = ringed;
  let lostSince: number | null = null;
  const tick = () => {
    // A newer undo/redo owns the editor now; chasing this field would fight its reveal.
    if (gen !== generation || performance.now() >= deadline) return;

    const present = findAnywhere(id, blockId);
    if (present) lostSince = null;
    else {
      /**
       * Gone, and given a moment to come back.
       *
       * A remount takes a frame or two, and that is what this is for. But a block the teacher
       * closed is gone for good, and a ring that kept asking for it would keep re-running the
       * reveal — re-expanding the next card to carry the same id, in the next block or the next
       * test. Chase a remount, not a ghost.
       */
      lostSince ??= performance.now();
      if (performance.now() - lostSince > LOST_GRACE_MS) return;
      revealField(id);
    }

    const now = findAnywhere(id, blockId);
    if (now && now !== current && isReachable(now)) { applyRing(now); current = now; }
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

function pulse(el: HTMLElement, id: string, scroll = true, blockId?: string): void {
  if (scroll) scrollIntoViewIfNeeded(el);
  applyRing(el);
  keepRinged(id, el, generation, blockId);
}

/**
 * One ring, on the thing that changed.
 *
 * This used to ring every field a step restored, up to six, on the reasoning that it was honest
 * about what the undo did. It was honest about the wrong thing: one click on "accept integer
 * answers only" changes six fields, and lighting all six says "something happened here" in six
 * places rather than "this is what you changed" in one. The decision (contract D2) is as specific
 * as possible — the act, not its consequences, and never a container alongside its own contents.
 */
/**
 * How long to keep looking before giving up.
 *
 * A field is reliably absent for the first frames after an undo and reliably present a few frames
 * later: the feedback panel has to mount, a collapsed card has to expand, and a card registers how
 * to expand itself only once it is mounted. This used to be a single fixed `setTimeout(…, 60)` in
 * the caller, which is a race with rendering whose timing nothing guarantees: too early and a
 * TAGGED field reports as missing.
 */
const REVEAL_DEADLINE_MS = 600;

/**
 * How long to wait for the BLOCK's editor to exist, before starting to look for a field inside it.
 *
 * Measured, not guessed: an undo that changes a block the teacher does not currently have open sets
 * the selection and then immediately asks for a field. The panel has to build that block's editor
 * first — a rich-text editor and everything around it — and on a real task that takes longer than
 * the deadline below. Both failures in a recorded session looked identical: asked for `question`,
 * gave up after 603ms and 604ms, on steps whose data had moved perfectly well. The page then
 * flashes the whole block, so a change that DID happen is reported as if nothing could be found.
 *
 * Kept separate from the field deadline because it is a different question. Waiting for a panel
 * that is being built is not the same as waiting for a field that may not exist, and rolling them
 * into one number would either cut the mount short or make a genuinely missing field slow.
 */
const PANEL_MOUNT_DEADLINE_MS = 3000;

/**
 * Resolve once this block can be searched — its editor has registered — or the budget runs out.
 *
 * Gives up quietly rather than failing: a block with no editor panel at all (the collapsed card is
 * the only thing on screen) still has fields worth ringing, and the ladder handles that already.
 */
function waitForPanel(blockId: string | undefined, gen: number): Promise<void> {
  if (!blockId || blockRevealers.has(blockId)) return Promise.resolve();
  return new Promise((resolve) => {
    const started = performance.now();
    const tick = () => {
      if (gen !== generation || blockRevealers.has(blockId)) { resolve(); return; }
      if (performance.now() - started >= PANEL_MOUNT_DEADLINE_MS) { resolve(); return; }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  });
}

/**
 * Undo and redo can arrive faster than a reveal completes.
 *
 * Each call takes the next number and stops as soon as a later one starts. Without this, a click
 * landing mid-reveal left the earlier ladder polling for an element the remount had just taken
 * away: it gave up, and its caller flashed the whole block — so clicking quickly through a stack
 * replaced precise highlights with whole-card flashes. A superseded ladder also leaves the surfaces
 * alone, because the newer one owns them now.
 */
let generation = 0;

/**
 * Stand every in-flight reveal down, the way a newer undo would.
 *
 * The reveal registry is keyed by FIELD id and nothing else, which is what lets one carousel and
 * one feedback card use the same mechanism — and also means two editors holding the same state id
 * are indistinguishable to it. A ring still chasing a field whose block has gone will happily
 * re-expand the next card to carry that id. In the product a newer undo supersedes it; anything
 * tearing an editor down wants the same, and now has it.
 */
export function cancelReveals(): void {
  generation++;
}

type Hit = { el: HTMLElement; id: string; reachable: boolean } | 'superseded' | null;

/**
 * Poll for any of `ids`.
 *
 * `acceptHidden` is the difference between the two questions this is asked. The first is "where is
 * this field?", and a mounted-but-hidden answer is worth having at once — it names the surface to
 * open. The second is "has the surface I just opened made it reachable?", and there a hidden answer
 * is the stale pre-render state: returning it immediately meant the ladder opened the drawer, was
 * handed back the still-collapsed element on the very next frame, concluded it had failed and shut
 * the drawer again.
 */
function waitForAny(
  ids: string[], deadlineMs: number, gen: number, acceptHidden: boolean,
  /** Whose fields to prefer when an id is ambiguous — see `findAnywhere`. */
  blockId?: string,
  /**
   * Re-asserted every frame, for the same reason the reveals are.
   *
   * An undo REMOUNTS the panel, so the frame that was asked to open its drawer can be replaced by
   * a fresh one — seeded shut — while the poll is still running. Asking once meant the request
   * landed on a component that no longer existed, the poll timed out, and the whole block flashed
   * instead. Opening is idempotent, so re-asserting it simply survives the remount.
   */
  ensure?: () => boolean,
): Promise<Hit> {
  return new Promise((resolve) => {
    const started = performance.now();
    const revealed = new Set<string>();
    /**
     * A container hit that is being passed over in the hope its own contents turn up.
     *
     * Kept rather than discarded so that if they never do, the ring still lands somewhere real
     * instead of falling back to flashing the whole block.
     */
    let fallback: Hit | null = null;
    const tick = () => {
      if (gen !== generation) { resolve('superseded'); return; }
      // Scheduled up front: a branch below returns early to wait for a reveal it has just asked
      // for, and must not drop the poll by doing so.
      const again = () => { if (performance.now() - started < deadlineMs) requestAnimationFrame(tick); else resolve(fallback); };

      /**
       * Reveals are re-attempted every frame, not once up front, because the thing that knows how
       * to reveal a field may not exist yet.
       *
       * A feedback card registers how to expand itself when it MOUNTS, and no card is mounted while
       * the panel is shut. Running the reveals once at the start therefore did nothing for them,
       * and the ladder settled for the card — collapsed, showing only its title — instead of the
       * message that had actually changed. Reveals are idempotent, so retrying is free.
       */
      let acted = ensure?.() ?? false;
      for (const id of ids) {
        if (revealed.has(id)) continue;
        if (revealField(id)) { revealed.add(id); acted = true; }
      }
      // Anything that just fired needs a frame to render before the tree is judged. Otherwise a
      // surface is opened and inspected in the same breath — it still looks shut — or the coarser
      // id wins the race against the finer one a reveal was about to uncover.
      if (acted) { again(); return; }

      /**
       * Priority order wins, and reachability is something to FIX rather than a tie-breaker.
       *
       * This used to return the first id that happened to be reachable, anywhere in the list. So a
       * click on a switch inside a shut options drawer rang whichever consequence of it lived out
       * in the open — the teacher toggled "accept integer answers only" and the error-mode control
       * lit up. The caller opens the surface for a hidden winner instead.
       */
      /** Is some id ahead of this one a field INSIDE it, still waiting to appear? */
      const holdsAPendingField = (id: string) =>
        ids.slice(0, ids.indexOf(id)).some(finer => finer.startsWith(`${id}.`) && !findAnywhere(finer, blockId));

      for (const id of ids) {
        const el = findAnywhere(id, blockId);
        if (!el) {
          /**
           * A higher-priority id whose reveal has been asked for, but which has not appeared yet.
           *
           * Settling for the next id here is how an edit to a feedback MESSAGE came to ring the
           * card holding it: expanding the card is what brings `<state>.content` into existence,
           * and on the frame the expansion was requested the card itself was already there and
           * looked like a perfectly good answer. Priority is worth waiting for — until the
           * deadline, after which anything visible beats nothing.
           */
          if (revealed.has(id)) { again(); return; }
          continue;
        }
        /**
         * A container never wins while its own contents are still on the way.
         *
         * A collapsed feedback card is in the document a frame before it registers how to expand
         * itself — registration is a passive effect, and the DOM is queried on the animation frame
         * in between. So the card looked like a perfectly good answer for a step that named the
         * MESSAGE inside it, and the ring landed on a card showing a title and nothing else.
         * `revealed` cannot cover this: on that frame nothing has been asked to reveal yet.
         */
        if (holdsAPendingField(id)) { fallback ??= { el, id, reachable: isReachable(el) }; again(); return; }
        if (isReachable(el)) { resolve({ el, id, reachable: true }); return; }
        if (acceptHidden) { resolve({ el, id, reachable: false }); return; }
        break;
      }
      again();
    };
    requestAnimationFrame(tick);
  });
}

/**
 * The options drawer is open when the step being shown lives in it, and shut otherwise.
 *
 * Stated as one rule applied to whatever the reveal ended up doing, rather than as a call on the
 * paths that happen to succeed. It used to be the latter, and the paths it was missing are exactly
 * where it went wrong: a field that could not be found flashes the whole block and left the drawer
 * as it was, a step that named no fields returned before any of this, and a field hidden behind a
 * surface that is not the drawer returned too. In each case a drawer opened for an earlier step
 * stayed open over the feedback the current step was trying to show — reported as "I undid into
 * another block and its options tray was open, hiding the feedback".
 *
 * Not applied to a SUPERSEDED reveal: a newer one owns the surfaces by then, and shutting a drawer
 * it has just opened would undo its work.
 */
function settleSurfaces(ringed: HTMLElement | null, blockId?: string): void {
  if (!blockId) return;
  if (ringed?.closest('[data-reveal-surface="settings"]')) return;
  closeRevealedSurfaces(blockId);
}

/**
 * Shut whatever a reveal opened, leaving the block as the teacher would find it.
 *
 * Used by the rule above, and by anything that needs a known starting state — a probe that walks
 * every field of a block in turn has to, or each answer depends on which surface the previous
 * question happened to leave open.
 */
export function closeRevealedSurfaces(blockId: string): void {
  const r = blockRevealers.get(blockId);
  if (r?.isSettingsOpen()) r.closeSettings();
}

/**
 * Put a block's surfaces into a named configuration, and run every registered reveal.
 *
 * For asking whether a field is TAGGED — a different question from what a reveal does, and one
 * that deserves a deterministic instrument. Driving the ladder once per field to answer it meant
 * ~190 probes sharing one editor, each leaving a surface open or shut for the next, and the answer
 * stopped being stable.
 *
 * Both surfaces cannot be shown at once: the feedback panel REPLACES the editing view rather than
 * sitting beside it, so a caller wanting the whole vocabulary of a block has to look twice.
 */
export function setSurfaces(blockId: string, want: { feedback: boolean; settings: boolean }): void {
  const r = blockRevealers.get(blockId);
  if (!r) return;
  if (r.isFeedbackOpen() !== want.feedback) (want.feedback ? r.openFeedback : r.closeFeedback)();
  if (r.isSettingsOpen() !== want.settings) (want.settings ? r.openSettings : r.closeSettings)();
  for (const reveal of revealers.values()) reveal();
}

export type RevealOutcome =
  /** Found and highlighted. */
  | 'pulsed'
  /** Not on this page at all — the caller should fall back to pulsing the block. */
  | 'missing'
  /** A newer undo/redo took over; it owns the surfaces, so the caller must do nothing. */
  | 'superseded';

/**
 * Point at whichever of these fields are findable, wherever they are hiding.
 *
 * Undo and redo use this to say WHICH field they took back, rather than pulsing the whole block and
 * leaving a teacher to guess. The reveal is the point: a change can be sitting behind a shut
 * settings drawer, inside a collapsed feedback card, behind the editor/feedback toggle, or on a
 * carousel page that is not showing — and an undo you cannot see is worse than one that moves the
 * view, because the state has already moved either way.
 *
 * The ids are tried in order, so the caller's priority is honoured: `changedFields` names the field
 * inside an item before the item itself, and item ids before the key of the list holding them.
 *
 * It does NOT hunt. An earlier version opened each surface in turn to see whether the field was
 * behind it, which a teacher watched happen: the feedback panel flicking open and shut on the way
 * to the options drawer, a second of it. Almost everything that hides a field keeps it mounted, so
 * the field can be found first and the surface that owns it read off the tree — see
 * `data-reveal-surface`. Only the feedback panel unmounts its contents, so only that one is ever
 * opened speculatively, and only when the field is nowhere at all.
 */
export async function pulseFirstField(
  fieldIds: string[],
  blockId?: string,
  // Overridable so a test can exercise this exact ladder without waiting out the poll.
  deadlineMs: number = REVEAL_DEADLINE_MS,
): Promise<RevealOutcome> {
  const gen = ++generation;
  /** What was ringed, so the surfaces can be settled once against it — `null` for nothing. */
  let ringed: HTMLElement | null = null;

  const outcome = await (async (): Promise<RevealOutcome> => {
    if (!fieldIds.length) return 'missing';

    // The editor for this block may still be building. Looking for a field inside a panel that does
    // not exist yet is how a perfectly good undo came to report itself as missing.
    await waitForPanel(blockId, gen);
    if (gen !== generation) return 'superseded';

    const hit = await waitForAny(fieldIds, deadlineMs, gen, true, blockId);
    if (hit === 'superseded') return 'superseded';

    if (hit?.reachable) { pulse(hit.el, hit.id, true, blockId); ringed = hit.el; return 'pulsed'; }

    // Mounted, but behind something. Open exactly that, rather than trying each surface in turn.
    if (hit) {
      const surface = hit.el.closest<HTMLElement>('[data-reveal-surface]');
      const owner = surface?.dataset.revealBlock;
      if (owner && surface?.dataset.revealSurface === 'settings') {
        const opened = await waitForAny([hit.id], deadlineMs, gen, false, blockId,
          () => { const r = blockRevealers.get(owner);
            if (!r || r.isSettingsOpen()) return false;
            r.openSettings(); return true; });
        if (opened === 'superseded') return 'superseded';
        if (opened?.reachable) { pulse(opened.el, opened.id, true, blockId); ringed = opened.el; return 'pulsed'; }
      }
      return 'missing';
    }

    // Nowhere in the document, and the feedback panel is the one surface that UNMOUNTS what it
    // holds — so it is the only thing worth moving on spec. Which way to move it depends on which
    // side is showing, and for a long time only one direction existed.
    const r = blockId ? blockRevealers.get(blockId) : undefined;
    if (r && !r.isFeedbackOpen()) {
      const behind = await waitForAny(fieldIds, deadlineMs, gen, false, blockId,
        () => { const rr = blockRevealers.get(blockId!);
          if (!rr || rr.isFeedbackOpen()) return false;
          rr.openFeedback(); return true; });
      if (behind === 'superseded') return 'superseded';
      if (behind?.reachable) { pulse(behind.el, behind.id, true, blockId); ringed = behind.el; return 'pulsed'; }
      blockRevealers.get(blockId!)?.closeFeedback();
    } else if (r) {
      /**
       * The other direction, which used to be missing entirely.
       *
       * The feedback panel replaces the editing view rather than sitting beside it, so while it is
       * open the question editor and every setting are simply not in the document. Undo a question
       * edit with the feedback tab showing and the ladder searched, found nothing, and the page
       * flashed the whole block — a change that HAD been applied, reported as if nothing could be
       * pointed at. Measured in a recorded session: both failures asked for `question`, and both
       * sat directly beside a feedback reveal.
       *
       * Put back if the field was not over there either, for the same reason the branch above puts
       * the panel back: the run should leave the surfaces where the teacher would expect them.
       */
      const infront = await waitForAny(fieldIds, deadlineMs, gen, false, blockId,
        () => { const rr = blockRevealers.get(blockId!);
          if (!rr || !rr.isFeedbackOpen()) return false;
          rr.closeFeedback(); return true; });
      if (infront === 'superseded') return 'superseded';
      if (infront?.reachable) { pulse(infront.el, infront.id, true, blockId); ringed = infront.el; return 'pulsed'; }
      blockRevealers.get(blockId!)?.openFeedback();
    }
    return 'missing';
  })();

  if (outcome !== 'superseded') settleSurfaces(ringed, blockId);
  recordUndoEvent('reveal', {
    blockId, asked: fieldIds, outcome,
    rang: ringed ? (ringed as HTMLElement).getAttribute('data-field-id') : null,
    drawerLeftOpen: blockId ? !!blockRevealers.get(blockId)?.isSettingsOpen() : undefined,
  });
  return outcome;
}

/**
 * Ring a field that is ALREADY on screen, with no ladder behind it.
 *
 * The ladder rings exactly one field and claims a generation while it does, so a second call to it
 * supersedes the first — correct for an undo, which points at one change, and wrong for "the
 * editor has moved to this version AND this branch", where both moved and both need saying.
 *
 * So the ladder handles the field that might need a surface opened, and this rings the other one
 * afterwards, by which point the surfaces are settled and it is simply present. No `keepRinged`:
 * this is for a field the caller already knows is mounted and staying.
 */
export function pulseVisibleField(fieldId: string): boolean {
  const el = document.querySelector<HTMLElement>(`[data-field-id="${CSS.escape(fieldId)}"]`);
  if (!el) return false;
  applyRing(el);
  return true;
}

/** Point at one field. */
export const pulseField = (fieldId: string, blockId?: string): Promise<RevealOutcome> =>
  pulseFirstField([fieldId], blockId);
